import React from 'react';

import {VATBoxType, ReturnPeriodTimeType} from '../../../utils/enums';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import LANGUAGE_CONTROLLER from '../../../controllers/languages';
import ApplicationMessages from '../../inAppMessages/ApplicationMessages';
import HighlightOnInAppMessage from '../../inAppMessages/HighlightOninAppMessage';
import FormattedCurrencyValue from '../../fields/number/FormattedCurrencyValue';

import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../../utils/logger';
import { withFullStoryExclude } from '../../../utils/fullStoryHelper';
import { SSelect } from '@avalara/skylab-sdk/react';

const FORM_NAME = 'MANUAL_DOCUMENTS_VAT_RETURNS_FORM';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);

const countriesWithSeparateMonthlyQuarterlyBoxes = [ 'IT' ];

const SingleBoxNameSelector = function (props) {

    const vatBoxes = props.vatBoxes || [];
    const defaultValue = props.defaultValue || '';
    const onChange = typeof props.onChange === 'function' ? props.onChange : (()=>{});
    const selectValue = props.selectValue || '';
    
    // Create options list for SSelect
    const optionsList = [
        {
            key: '',
            value: '',
            label: LANGUAGE_CONTROLLER.format(props.languageString, "singleBoxNameSelector_SelectBoxName"),
            selected: selectValue === ''
        },
        ...vatBoxes.map((box, index) => ({
            key: index.toString(),
            value: box.boxName,
            label: box.boxName,
            selected: selectValue === box.boxName
        }))
    ];

    return (
        <SSelect
            inputId="single-box-name-selector"
            optionsList={optionsList}
            multiple={false}
            onS-select={(e) => {
                // Create a synthetic event that matches the original onChange signature
                const syntheticEvent = {
                    target: { value: e.detail.item.value }
                };
                onChange(syntheticEvent);
            }}
        />
    );
};


const DEFAULT_SELECTION = {
    boxType: VATBoxType.TAXABLE_BASIS.name,
    boxName: '',
    boxValue: '',
    returnType: ReturnPeriodTimeType.MONTHLY.name
};

class ManualDocumentsVATReturnsForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            selection: DEFAULT_SELECTION
        };
        this.onAddHandler = this.onAddHandler.bind(this);
        this.resetToDefaults = this.resetToDefaults.bind(this);
    }

    resetToDefaults() {
        this.setState({
            selection: DEFAULT_SELECTION
        })
    }

    onAddHandler() {
        log.debug('onAddHandler(): ');
        const selection = Object.assign({}, this.state.selection);
        log.debug('onAddHandler(): selection: ', selection);
        log.clearDictionaryError(ALL_MESSAGES.BLANK_BOX_NAME);
        log.clearDictionaryError(ALL_MESSAGES.BLANK_BOX_VALUE);
        let fieldsOk = true;
        const boxName = selection.boxName && selection.boxName.trim();
        const boxValue = selection.boxValue && selection.boxValue.trim();
        if (!boxName || boxName === '') {
            log.setDictionaryMessage(ALL_MESSAGES.BLANK_BOX_NAME);
            fieldsOk = false;
        }
        if (!boxValue || boxValue === '') {
            log.setDictionaryMessage(ALL_MESSAGES.BLANK_BOX_VALUE);
            fieldsOk = false;
        }
        if(fieldsOk && typeof this.props.onAddClick === 'function'){
            this.props.onAddClick(selection);
            if(this.props.resetFormAfterAdd) {
                this.resetToDefaults();
            }
        }
    }
    
    onFieldChangeHandlerFactory(fieldName) {
        log.debug('onFieldChangeHandlerFactory(): fieldName: ', fieldName);
        return (event) => {
            let selection = Object.assign({}, this.state.selection);
            selection[fieldName] = event.target.value;
            this.setState({selection: selection});
        };
    }

    currencyChangeHandler(fieldNameValue) {
        log.debug('currencyChangeHandler(): fieldNameValue: ', fieldNameValue);
        let selection = Object.assign({}, this.state.selection, fieldNameValue);
        this.setState({selection: selection});
    }

    selectionHandler(fieldName) {
        return (event) => {
            log.debug('selectionHandler(%s): %s, ', fieldName, event);
            log.debug('selectionHandler(%s): newValue: %s  ', fieldName, event.target.value);
            let selection = Object.assign({}, this.state.selection);
            selection[fieldName] = event.target.value;
            this.setState({selection: selection});
        };
    }

    render() {
        log.debug('render(): ', this.props);
        const selection = this.state.selection || {};
        const vatBoxes = this.props.vatBoxes || [];
        const boxTypeEnum = VATBoxType.enumValueOf(selection.boxType);
        const returnTypeEnum = ReturnPeriodTimeType.enumValueOf(selection.returnType);
        const vatBoxesFilteredByReturnTypeAndBoxType = vatBoxes.filter( (vatBox) => {
            const isSelectedVATBoxType = boxTypeEnum && boxTypeEnum.value === vatBox.boxType;
            const isSelectedReturnType = returnTypeEnum && returnTypeEnum.value === vatBox.returnFrequency;
            return isSelectedVATBoxType && isSelectedReturnType;
        });

        return (
            <fieldset>
                <h3 className="mb10"><LanguageStringResolver string='manualDocumentsVATReturnsForm_AddVATBox'/></h3>
                <s-row>
                    <s-col span="6">
                        <ApplicationMessages 
                            scopes={[FORM_NAME]} 
                            messageIds={[ALL_MESSAGES.BLANK_BOX_NAME, ALL_MESSAGES.BLANK_BOX_VALUE]} 
                            messageType="applicationError"
                        />
                        <legend><LanguageStringResolver string='manualDocumentsVATBoxList_BoxType'/>:</legend>  
                        <input
                            className="radio" 
                            type="radio" 
                            name="boxType" 
                            onChange={this.selectionHandler('boxType')} 
                            value={VATBoxType.TAXABLE_BASIS.name} 
                            checked={selection.boxType === VATBoxType.TAXABLE_BASIS.name}/>
                        <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_TaxableBasis'/></label>
                        <input
                            className="radio" 
                            type="radio" 
                            name="boxType" 
                            onChange={this.selectionHandler('boxType')} 
                            value={VATBoxType.VAT.name} 
                            checked={selection.boxType === VATBoxType.VAT.name}/>
                        <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_VAT'/></label>
                        <span className="clear"></span>
                        <legend className="mt10"><LanguageStringResolver string='manualDocumentsVATReturnsForm_ReturnType'/>:</legend>
                        { countriesWithSeparateMonthlyQuarterlyBoxes.includes(this.props.selectedCountry) ?
                            <div>
                              <input
                                className="radio"
                                type="radio"
                                name="returnType"
                                value={ReturnPeriodTimeType.MONTHLY.name} onChange={this.selectionHandler('returnType')}
                                checked={selection.returnType === ReturnPeriodTimeType.MONTHLY.name}
                              />
                              <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_Monthly'/></label>
                              <input
                                className="radio"
                                type="radio"
                                name="returnType"
                                value={ReturnPeriodTimeType.QUARTERLY.name} onChange={this.selectionHandler('returnType')}
                                checked={selection.returnType === ReturnPeriodTimeType.QUARTERLY.name}
                              />
                              <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_Quarterly'/></label>
                            </div>
                          :
                            <div>
                              <input
                                className="radio"
                                type="radio"
                                name="returnType"
                                value={ReturnPeriodTimeType.MONTHLY.name} onChange={this.selectionHandler('returnType')}
                                checked={selection.returnType === ReturnPeriodTimeType.MONTHLY.name}
                              />
                              <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_MonthlyQuarterly'/></label>
                            </div>
                        }
                        <input
                            type="radio"
                            name="returnType" 
                            value={ReturnPeriodTimeType.YEARLY.name}
                            onChange={this.selectionHandler('returnType')}
                            checked={selection.returnType === ReturnPeriodTimeType.YEARLY.name}
                        />
                        <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_Yearly'/></label>
                        <input
                            className="radio"
                            type="radio"
                            name="returnType" 
                            value={ReturnPeriodTimeType.YEARLY2.name}
                            onChange={this.selectionHandler('returnType')}
                            checked={selection.returnType === ReturnPeriodTimeType.YEARLY2.name}
                        />
                        <label className="radio"><LanguageStringResolver string='manualDocumentsVATReturnsForm_Yearly2'/></label>
                    </s-col>
                    <div>
                        <HighlightOnInAppMessage
                            scopes={[FORM_NAME]}
                            messageIds={[ALL_MESSAGES.BLANK_BOX_NAME]}
                        >
                            <label><LanguageStringResolver string='manualDocumentsVATReturnsForm_BoxName'/>:</label>
                            <SingleBoxNameSelector 
                                vatBoxes={vatBoxesFilteredByReturnTypeAndBoxType}
                                onChange={this.selectionHandler('boxName')}
                                defaultValue={''}
                                selectValue={selection.boxName}
                            />   
                        </HighlightOnInAppMessage>
                            
                        <HighlightOnInAppMessage
                            scopes={[FORM_NAME]}
                            messageIds={[ALL_MESSAGES.BLANK_BOX_VALUE]}
                        >
                            <label className="mt10"><LanguageStringResolver string='manualDocumentsVATReturnsForm_BoxValue'/>:</label>
                            <FormattedCurrencyValue inputFieldClassName={withFullStoryExclude()} rightAlign={false} value={selection.boxValue} editOptions={
                                {
                                    fieldName: 'boxValue',
                                    changeHandler: this.currencyChangeHandler.bind(this)
                                }
                            } />
                        </HighlightOnInAppMessage>
                    </div> 
                </s-row>
                <a className='button secondary mt10' onClick={this.onAddHandler}>
                    <LanguageStringResolver string='manualDocumentsLineEditor_Add' />
                </a>
            </fieldset>
        );
    }
}



ManualDocumentsVATReturnsForm.defaultProps = {
    onAddClick: () => {},
    vatBoxes: [],
    resetFormAfterAdd: true,
    selectedCountry: ''
};

export { SingleBoxNameSelector };
export default ManualDocumentsVATReturnsForm;